// Evaluador aritmético seguro para las fórmulas de la configuración de plantillas.
// Soporta + - * / y paréntesis sobre nombres de campo que se resuelven a números.
// No usa eval: tokeniza y evalúa con un descenso recursivo, así una fórmula mal
// formada devuelve null en vez de ejecutar nada.

type Token =
  | { kind: 'number'; value: number }
  | { kind: 'identifier'; value: string }
  | { kind: 'operator'; value: '+' | '-' | '*' | '/' }
  | { kind: 'paren'; value: '(' | ')' };

function tokenize(expression: string): Token[] | null {
  const tokens: Token[] = [];
  let i = 0;
  while (i < expression.length) {
    const char = expression[i];
    if (char === ' ' || char === '\t') {
      i++;
      continue;
    }
    if (char === '+' || char === '-' || char === '*' || char === '/') {
      tokens.push({ kind: 'operator', value: char });
      i++;
      continue;
    }
    if (char === '(' || char === ')') {
      tokens.push({ kind: 'paren', value: char });
      i++;
      continue;
    }
    if (/[0-9.]/.test(char)) {
      let number = '';
      while (i < expression.length && /[0-9.]/.test(expression[i])) {
        number += expression[i];
        i++;
      }
      const parsed = Number(number);
      if (isNaN(parsed)) return null;
      tokens.push({ kind: 'number', value: parsed });
      continue;
    }
    if (/[A-Za-z_]/.test(char)) {
      let identifier = '';
      while (i < expression.length && /[A-Za-z0-9_]/.test(expression[i])) {
        identifier += expression[i];
        i++;
      }
      tokens.push({ kind: 'identifier', value: identifier });
      continue;
    }
    //cualquier otro carácter invalida la fórmula entera
    return null;
  }
  return tokens;
}

/**
 * Evalúa una expresión aritmética resolviendo los identificadores contra `variables`.
 * Un identificador ausente o no numérico cuenta como 0. Devuelve null si la fórmula
 * no es válida o si hay una división por cero.
 */
export function evaluateFormula(
  expression: string,
  variables: Record<string, number>
): number | null {
  if (!expression) return null;
  const tokens = tokenize(expression);
  if (tokens === null) return null;

  let position = 0;

  const peek = (): Token | undefined => tokens[position];

  // expr := term (('+'|'-') term)*
  const parseExpression = (): number | null => {
    let left = parseTerm();
    if (left === null) return null;
    while (peek()?.kind === 'operator' && ((peek() as any).value === '+' || (peek() as any).value === '-')) {
      const operator = (tokens[position++] as any).value;
      const right = parseTerm();
      if (right === null) return null;
      left = operator === '+' ? left + right : left - right;
    }
    return left;
  };

  // term := factor (('*'|'/') factor)*
  const parseTerm = (): number | null => {
    let left = parseFactor();
    if (left === null) return null;
    while (peek()?.kind === 'operator' && ((peek() as any).value === '*' || (peek() as any).value === '/')) {
      const operator = (tokens[position++] as any).value;
      const right = parseFactor();
      if (right === null) return null;
      if (operator === '/') {
        if (right === 0) return null;
        left = left / right;
      } else {
        left = left * right;
      }
    }
    return left;
  };

  // factor := number | identifier | '(' expr ')' | '-' factor
  const parseFactor = (): number | null => {
    const token = peek();
    if (token === undefined) return null;
    if (token.kind === 'operator' && (token.value === '-' || token.value === '+')) {
      position++;
      const operand = parseFactor();
      if (operand === null) return null;
      return token.value === '-' ? -operand : operand;
    }
    if (token.kind === 'number') {
      position++;
      return token.value;
    }
    if (token.kind === 'identifier') {
      position++;
      const resolved = variables[token.value];
      return typeof resolved === 'number' && !isNaN(resolved) ? resolved : 0;
    }
    if (token.kind === 'paren' && token.value === '(') {
      position++;
      const inner = parseExpression();
      if (inner === null) return null;
      if (peek()?.kind !== 'paren' || (peek() as any).value !== ')') return null;
      position++;
      return inner;
    }
    return null;
  };

  const result = parseExpression();
  //si sobran tokens, la fórmula estaba mal formada
  if (result === null || position !== tokens.length) return null;
  if (!isFinite(result)) return null;
  return result;
}
